import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Sparkles } from 'lucide-react';
import logger from '../../utils/logger';

const DAYS = [
  { v: 0, label: 'Sun' },
  { v: 1, label: 'Mon' },
  { v: 2, label: 'Tue' },
  { v: 3, label: 'Wed' },
  { v: 4, label: 'Thu' },
  { v: 5, label: 'Fri' },
  { v: 6, label: 'Sat' },
];

const COLORS = ['#0077B6', '#6B3FA0', '#E07A5F', '#1B5E20', '#AD1457', '#E65100', '#5A4D6E'];

const EMPTY = {
  name: '', description: '', default_time_in: '17:00', default_time_out: '20:00',
  price_per_session: '', capacity: 8, color: '#0077B6', recurring_days: [], location: '', is_active: true,
};

export default function PrivatePayProgramsTab() {
  const { api } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await api.get('/private-pay/programs');
      setPrograms(data);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setDialog(true); };

  const save = async () => {
    if (!form.name?.trim()) { toast.error('Name required'); return; }
    try {
      if (editing) { await api.put(`/private-pay/programs/${editing.id}`, form); toast.success('Updated'); }
      else { await api.post('/private-pay/programs', form); toast.success('Program added'); }
      setDialog(false); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this program? This will not remove already-scheduled sessions.')) return;
    await api.delete(`/private-pay/programs/${id}`); fetchAll();
  };

  const toggleDay = (d) => {
    setForm(f => ({
      ...f,
      recurring_days: f.recurring_days.includes(d) ? f.recurring_days.filter(x => x !== d) : [...f.recurring_days, d].sort(),
    }));
  };

  return (
    <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="private-pay-programs">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Sparkles className="h-4 w-4 text-[#0077B6]" /> Private Pay Programs
        </CardTitle>
        <Button onClick={openCreate} className="bg-[#0077B6] hover:bg-[#005A8C] text-white gap-1 h-9" data-testid="pp-add-btn"><Plus className="h-4 w-4" /> Add Program</Button>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-[#8E85A0] mb-3">Evening / private pay programs (e.g., Cooking Class, Movie Night). These power the "Private Pay" tab on the schedule and feed monthly revenue projections in Finance.</p>
        {loading ? <p className="text-sm text-[#8E85A0]">Loading…</p> : programs.length === 0 ? (
          <div className="text-center py-6 text-[#8E85A0] border border-dashed border-[#DED8EB] rounded-lg">
            <Sparkles className="h-7 w-7 mx-auto mb-1.5 opacity-50" />
            <p className="text-sm">No programs yet — add one to start scheduling private pay sessions.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="pp-list">
            {programs.map(p => (
              <div key={p.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#DED8EB] bg-[#F7F4EF] hover:shadow-sm transition-shadow" data-testid={`pp-row-${p.id}`} style={{ borderLeftWidth: 4, borderLeftColor: p.color }}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-bold text-[#2D1B4E]">{p.name}</h3>
                    {!p.is_active && <Badge className="text-[10px] bg-[#F0EDF6] text-[#8E85A0] border-[#DED8EB]">Inactive</Badge>}
                    <span className="text-[11px] text-[#8E85A0] font-mono">{p.default_time_in} – {p.default_time_out}</span>
                    <span className="text-[11px] font-bold text-[#0077B6]">${(p.price_per_session || 0).toFixed(2)}/session</span>
                    <span className="text-[10px] text-[#8E85A0]">cap {p.capacity}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {(p.recurring_days || []).map(d => (
                      <span key={d} className="text-[10px] px-1.5 py-0.5 rounded bg-[#E1F5FE] text-[#01579B] font-semibold">{DAYS.find(x => x.v === d)?.label}</span>
                    ))}
                    {p.location && <span className="text-[10px] text-[#8E85A0]">📍 {p.location}</span>}
                  </div>
                  {p.description && <p className="text-[11px] text-[#5A4D6E] mt-1 italic">{p.description}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(p)} className="h-7 w-7 text-[#8E85A0] hover:text-[#0077B6]" data-testid={`pp-edit-${p.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(p.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`pp-del-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="sm:max-w-lg bg-white" data-testid="pp-dialog">
          <DialogHeader><DialogTitle className="text-lg font-bold text-[#2D1B4E]">{editing ? 'Edit Program' : 'New Private Pay Program'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto">
            <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g., Cooking Class" className="border-[#DED8EB]" data-testid="pp-name" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Short description" className="border-[#DED8EB]" data-testid="pp-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Default Start</Label><Input type="time" value={form.default_time_in} onChange={e => setForm(f => ({ ...f, default_time_in: e.target.value }))} className="border-[#DED8EB]" data-testid="pp-tin" /></div>
              <div><Label>Default End</Label><Input type="time" value={form.default_time_out} onChange={e => setForm(f => ({ ...f, default_time_out: e.target.value }))} className="border-[#DED8EB]" data-testid="pp-tout" /></div>
              <div><Label>Price / Session ($)</Label><Input type="number" step="0.01" value={form.price_per_session} onChange={e => setForm(f => ({ ...f, price_per_session: e.target.value }))} className="border-[#DED8EB]" data-testid="pp-price" /></div>
              <div><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={e => setForm(f => ({ ...f, capacity: parseInt(e.target.value) || 0 }))} className="border-[#DED8EB]" data-testid="pp-capacity" /></div>
              <div className="col-span-2"><Label>Location</Label><Input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g., Main Office Kitchen" className="border-[#DED8EB]" data-testid="pp-loc" /></div>
            </div>
            <div>
              <Label>Recurring Days</Label>
              <div className="flex gap-1 mt-1.5 flex-wrap">
                {DAYS.map(d => (
                  <button key={d.v} type="button" onClick={() => toggleDay(d.v)}
                    className={`h-9 w-12 rounded-md text-xs font-bold transition ${form.recurring_days.includes(d.v) ? 'bg-[#0077B6] text-white' : 'bg-[#F0EDF6] text-[#5A4D6E] hover:bg-[#E1F5FE]'}`}
                    data-testid={`pp-day-${d.v}`}>{d.label}</button>
                ))}
              </div>
            </div>
            <div>
              <Label>Color</Label>
              <div className="flex gap-1.5 mt-1.5">
                {COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setForm(f => ({ ...f, color: c }))}
                    style={{ width: 28, height: 28, borderRadius: 6, background: c, border: form.color === c ? '3px solid #2D1B4E' : '1px solid var(--line, #DED8EB)' }} />
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Switch checked={form.is_active} onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))} data-testid="pp-active" />
              <Label className="text-sm cursor-pointer">Active (show in scheduling)</Label>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button><Button onClick={save} className="bg-[#0077B6] hover:bg-[#005A8C] text-white" data-testid="pp-save-btn">{editing ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
